import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LOCALES, MESSAGES, type Locale } from '@rozman/shared';

/**
 * All copy comes from `packages/shared`, so storefront, admin and API error messages draw
 * from one source. There are no hardcoded strings anywhere in this app — a grep for Polish
 * text in src/ returns nothing but this file's imports.
 */
export const STORAGE_KEY = 'rozman.locale';

export function detectLocale(pathname: string): Locale {
  const fromPath = pathname.split('/')[1];
  if (LOCALES.includes(fromPath as Locale)) return fromPath as Locale;
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored && LOCALES.includes(stored as Locale)) return stored as Locale;
  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'pl';
  return LOCALES.includes(browser as Locale) ? (browser as Locale) : 'pl';
}

void i18next.use(initReactI18next).init({
  resources: Object.fromEntries(LOCALES.map((locale) => [locale, { translation: MESSAGES[locale] }])),
  lng: typeof window !== 'undefined' ? detectLocale(window.location.pathname) : 'pl',
  // Polish is the fallback locale. An untranslated key shows the Polish string, never a
  // raw key and never a blank.
  fallbackLng: 'pl',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

/** Polish plural rules: 1 / 2-4 / 5+. i18next handles this via _one/_few/_many suffixes. */
export function applyLocale(locale: Locale): void {
  void i18next.changeLanguage(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, locale);
}

export default i18next;
